/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import  { useCallback} from 'react';
import Typography from '@mui/material/Typography';
import EyeVisible from '../../icons/EyeVisible.svg?react';
import EyeHidden from '../../icons/EyeHidden.svg?react';
import ZoomToArea from '../../icons/ZoomToArea.svg?react';
import CircularProgress from '@mui/material/CircularProgress';
import mikeSharedTheme, { mikePalette } from '../../styles/mikeSharedTheme';
import ILegendLayer, { ILayerGroup } from '../../model/ILegendLayer';
import { layerHidden, layerShown } from '../../reducers/legend';
import { useAppDispatch } from '../../store/store';
import { _getRgbArray } from './utils';
import MikeVisualizerLib from '../../MikeVisualizer/lib/MikeVisualizer';
import { IViewerBounds } from '../../MikeVisualizer/lib/IMikeVisualizerModels';
import { BASEMAP_ID, NEW_MEASURES_ID, RIVER_ID } from '../../model/layerGroups';

const iconPrimaryStyle = (disabled = false) => {
  const color = disabled ? mikePalette.primary.light : mikePalette.primary.main
  return css`
    line {      
      stroke: ${color};
    }
    path {
      fill: ${color};
      stroke: ${color};
    }
  `
}
const TitleContainerStyle = (visible: boolean) => {
  const backgroundColor = visible ? mikePalette.background.paper : mikePalette.lightGrey?.main;  
  return css`
    display: flex;
    justify-content: space-between;
    align-items: center;  
    background-color: ${backgroundColor};
    border-top: 1px solid ${mikePalette.mediumGrey?.main};
  `;
}

export const TitleStyle = (visible: boolean) => {  
  const color = visible ? mikePalette.primary.main : mikePalette.primary.light;
  return css`
  padding-left: ${mikeSharedTheme.spacing(1)};
  padding-right: ${mikeSharedTheme.spacing(1)};
  &.MuiTypography-root {
    color: ${color};
  }  
`;
}

const buttonContainerStyle = css`
  display: flex;
  align-items: center;
`;

const colorStyle = (layer: ILegendLayer) => {   
  const color = layer.color;
  if (layer.isPoint) {
    return css`
      background-color: ${color};
      width: ${mikeSharedTheme.spacing(1.5)};
      height: ${mikeSharedTheme.spacing(1.5)};
      border-radius: ${mikeSharedTheme.spacing(1.5)};
    `
  }
  else if (layer.id === RIVER_ID){
    return css`
    width: ${mikeSharedTheme.spacing(2)};
    height: ${mikeSharedTheme.spacing(0.125)};
    border-style: solid;
    border-color: ${color};
  `;
  }
  return css`
    width: ${mikeSharedTheme.spacing(2)};
    height: ${mikeSharedTheme.spacing(2)};
    border-style: solid;
    border-color: ${color};
  `;
}

const spinnerContainerStyle = css`
  height: ${mikeSharedTheme.spacing(5)};
  padding-right: ${mikeSharedTheme.spacing(1.5)};
  display: flex;
  align-items: center;
`;

interface IProps {
  layerGroup: ILayerGroup;
  layer: ILegendLayer; 
}

export const Layer = (props: IProps) => {
  const dispatch = useAppDispatch()
  const {layer} = props;   

  const zoomToExtent = useCallback(async() => {
    const { get2DDataExtent, resetCameraToBounds } = MikeVisualizerLib; 
    const bounds = await get2DDataExtent(layer.id) // [minX, minY, maxX, maxY]
    const buffer = layer.id === NEW_MEASURES_ID ? 500 : 0
    const formattedBounds = 
       [
          bounds[0] - buffer,
          bounds[2] + buffer,
          bounds[1] - buffer,
          bounds[3] + buffer,
          0,
          0,
        ] 
   
    resetCameraToBounds(formattedBounds  as IViewerBounds);
  },[layer])

  const handleToggleVisibilty = useCallback(async () => {
    const changeToHide = layer.visible
    if (changeToHide){
     dispatch(layerHidden(layer))    
    }
    else{ 
      if (layer.loaded){
        dispatch(layerShown(layer))
      } 
    }
  }, [dispatch, layer])

  return (     
    <div>
      <div css={TitleContainerStyle(layer.visible)}> 
        <Typography css= {TitleStyle(layer.visible)}  variant="body2"><b>{layer.title}</b></Typography>
        <div css={buttonContainerStyle}>          
          {layer.color ? <div
            css={colorStyle(layer)}/> : null} 
          {layer.loading ? <div css={spinnerContainerStyle}><CircularProgress size={20}/></div> : layer.visible ? <div css={buttonContainerStyle}>{layer.id !== BASEMAP_ID && <ZoomToArea onClick={zoomToExtent}/>}<EyeVisible onClick={handleToggleVisibilty}/></div> : <EyeHidden css={iconPrimaryStyle(true)} onClick={handleToggleVisibilty} />}
        </div>
      </div>         
    </div>    
  );
};

export default Layer

